import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { useUserContext } from 'context/UserContextProvider';
import { format, startOfDay } from 'date-fns';
import { displayRow } from 'helpers/general';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import useDeleteLegalDocumentVersionMutation from 'hooks/mutations/useDeleteLegalDocumentMutation';
import { useLegalDocumentQuery } from 'hooks/useLegalDocumentQuery';
import { ContentCard } from 'layout/ContentCard';
import PageMetatags from 'layout/PageMetatags';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MdDownload } from 'react-icons/md';
import { Link, useNavigate, useParams } from 'react-router-dom';

const LegalDocumentDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const toast = useToast();
  const legalDocumentId = Number(id);
  const navigate = useNavigate();
  const { user } = useUserContext();
  const legalDocumentQuery = useLegalDocumentQuery(legalDocumentId);
  const legalDocument = legalDocumentQuery?.data?.data;

  const deleteDocumentMutation = useDeleteLegalDocumentVersionMutation(legalDocumentId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const finalRef = useRef(null);

  const formatDate = (date: string | null | undefined, dateFormat: string = 'dd.MM.yyyy') => {
    if (date == null) return '';
    return format(startOfDay(new Date(date)), dateFormat);
  };

  const statusTagColor = {
    outdated: 'gray',
    planned: 'blue',
    enrolled: 'green',
  } as const;

  if (isNaN(legalDocumentId)) {
    navigate('/404', { replace: true });
    return null;
  }
  return (
    <Box>
      <PageMetatags title="Legal document version" />
      <ContentCard header="Legal document details">
        {legalDocumentQuery.isLoading && <LoadingSpinner />}
        {legalDocumentQuery.isSuccess && (
          <>
            <Tag
              colorScheme={statusTagColor[legalDocument?.status as keyof typeof statusTagColor]}
              variant={legalDocument?.status != 'outdated' ? 'subtle' : 'outline'}
            >
              {t(`legal_documents:legal_document_properties.statuses.${legalDocument?.status}`)}
            </Tag>

            <Stack py={8}>
              <Stack>
                {displayRow({
                  label: t('legal_documents:legal_document_properties.type'),
                  content: t(`legal_documents:legal_document_properties.types.${legalDocument?.type}`),
                  isLoading: legalDocumentQuery?.isLoading,
                })}
                {displayRow({
                  label: t('legal_documents:legal_document_properties.version'),
                  content: legalDocument?.version,
                  isLoading: legalDocumentQuery?.isLoading,
                })}
                {displayRow({
                  label: t('legal_documents:legal_document_properties.effective_at'),
                  content: formatDate(legalDocument?.effective_at, 'MM-yyyy'),
                  isLoading: legalDocumentQuery?.isLoading,
                })}
                {displayRow({
                  label: t('legal_documents:legal_document_properties.created_at'),
                  content: formatDate(legalDocument?.created_at ?? undefined),
                  isLoading: legalDocumentQuery?.isLoading,
                })}
                {displayRow({
                  label: t('legal_documents:legal_document_properties.updated_at'),
                  content: formatDate(legalDocument?.updated_at ?? undefined),
                  isLoading: legalDocumentQuery?.isLoading,
                })}
                <Flex gap="2" mt="6">
                  <a href={legalDocument?.en_file_url}>
                    <Button leftIcon={<MdDownload />} size="sm" onClick={() => {}}>
                      {t('legal_documents:legal_document_properties.versions.en')}
                    </Button>
                  </a>
                  <a href={legalDocument?.de_file_url}>
                    <Button leftIcon={<MdDownload />} size="sm">
                      {t('legal_documents:legal_document_properties.versions.de')}
                    </Button>
                  </a>
                </Flex>
              </Stack>
            </Stack>
          </>
        )}
        <Flex justifyContent="flex-end" gap="2">
          {user.permissions.includes('legal-document-versions:delete') && (
            <Button variant="subtle" color="red" border="1px solid red" onClick={onOpen} isDisabled={legalDocument?.status !== 'planned'}>
              {t('legal_documents:legal_document_details.button_delete')}
            </Button>
          )}
          {user.permissions.includes('legal-document-versions:update') && (
            <Button variant="primary" as={Link} to={`/app/op/legal-document-version/${legalDocumentId}/update`}>
              {t('legal_documents:legal_document_details.button_update')}
            </Button>
          )}
        </Flex>

        <Modal finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('legal_documents:legal_document_details.modal.title')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>{t('legal_documents:legal_document_details.modal.body')}</ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                {t('legal_documents:legal_document_details.modal.button_secondary')}
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  deleteDocumentMutation.mutate(undefined as never, {
                    onSuccess: () => {
                      navigate('/app/op/legal-documents', { replace: true });
                    },
                    onError: (error: any) => {
                      close;
                      const { message } = error.response.data;
                      manageErrorResponse(toast, message);
                    },
                  });
                }}
              >
                {t('legal_documents:legal_document_details.modal.button_primary')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </ContentCard>
    </Box>
  );
};

export default LegalDocumentDetailPage;
